import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { IntlProvider } from 'use-intl';
import { FirebaseContext } from 'context';
import {
  catalogues,
  defaultLanguage,
  formats,
  resolveEnabledLanguage,
  getDirection
} from './config';

export const LanguageContext = createContext({
  language: defaultLanguage,
  setLanguage: () => {}
});

export const useLanguage = () => useContext(LanguageContext);

const STORAGE_KEY = 'kol-lang';

// Deep-merge the default catalogue UNDER the active one so a missing key falls
// back to the default-language string through use-intl's normal ICU path. This
// is the fallback mechanism (spec §5.6) — NOT getMessageFallback, which returns
// a raw, non-ICU-parsed string. One level of namespaces, so merge per namespace.
function mergeCatalogues(base, override) {
  return Object.keys(override).reduce(
    (out, namespace) => {
      out[namespace] = { ...(base[namespace] || {}), ...override[namespace] };
      return out;
    },
    { ...base }
  );
}

// First client render (createRoot) reads localStorage/navigator here; during
// Gatsby's SSR build there is no window, so we fall back to default and let the
// client resolve. No hydration mismatch — the app client-renders (createRoot),
// it does not hydrate.
function resolveInitial() {
  if (typeof window === 'undefined') return defaultLanguage;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) return resolveEnabledLanguage(stored);
  return resolveEnabledLanguage(window.navigator?.language);
}

function handleIntlError(error) {
  // Missing keys are expected while catalogues are partial — warn in dev, stay
  // quiet in prod. Anything else is a real bug; surface it.
  if (error.code === 'MISSING_MESSAGE') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] ${error.message}`);
    }
    return;
  }
  // Expected: we deliberately configure no timeZone/now on the provider
  // (users span timezones; nothing timezone-sensitive is rendered), so
  // use-intl's informational environment-fallback notice is not an error.
  if (error.code === 'ENVIRONMENT_FALLBACK') {
    return;
  }
  // eslint-disable-next-line no-console
  console.error(error);
}

function getMessageFallback({ namespace, key }) {
  const path = [namespace, key].filter(Boolean).join('.');
  return process.env.NODE_ENV === 'production' ? '' : `⟦${path}⟧`;
}

export function LanguageProvider({ children }) {
  const { user, firebase } = useContext(FirebaseContext);
  const [language, setLanguageState] = useState(resolveInitial);

  // The persisted preference wins once auth resolves — but only apply it once,
  // so a mid-session switch (Stage 1) is never clobbered by the async user doc.
  const [appliedUserPref, setAppliedUserPref] = useState(false);
  useEffect(() => {
    if (appliedUserPref) return;
    const pref = user?.preferredLanguage;
    if (pref) {
      setLanguageState(resolveEnabledLanguage(pref));
      setAppliedUserPref(true);
    }
  }, [user, appliedUserPref]);

  const setLanguage = useCallback(
    (code) => {
      const resolved = resolveEnabledLanguage(code);
      setLanguageState(resolved);
      setAppliedUserPref(true);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, resolved);
      }
      // Fire-and-forget — never block the UI on the network. useAuth's
      // onSnapshot propagates the persisted value back onto `user`, and the
      // appliedUserPref flag above stops it re-applying over a newer choice.
      if (user?.uid && firebase?.userProfile) {
        firebase.userProfile
          .setPreferredLanguage({ uid: user.uid, language: resolved })
          .catch((error) => {
            console.error('[i18n] Could not persist language:', error);
          });
      }
    },
    [user?.uid, firebase]
  );

  // The only seam for <html lang>/dir — no Helmet/Gatsby-Head/html.js exists.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = language;
    document.documentElement.dir = getDirection(language);
  }, [language]);

  const messages = useMemo(() => {
    const active = catalogues[language] || catalogues[defaultLanguage];
    if (language === defaultLanguage) return active;
    return mergeCatalogues(catalogues[defaultLanguage], active);
  }, [language]);

  const ctx = useMemo(
    () => ({ language, setLanguage }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={ctx}>
      <IntlProvider
        locale={language}
        messages={messages}
        formats={formats}
        onError={handleIntlError}
        getMessageFallback={getMessageFallback}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
}
